import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button, Drawer, Badge, Checkbox, colors } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import EventCard from './EventCard';
import * as actions from '../../../../store/actions';
import { EAccessRole, RootState } from '../../../../store/types';
import { i18n } from '../../../../translate/i18n';
import ServicesForSchedulingFiltersModal from '../Modals/ServicesForSchedulingFiltersModal';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

type Props = {
  goToEquipmentPage: (equipmentId: string) => void,
}

const EventCardList: React.FC<Props> = ({ goToEquipmentPage }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState<number>(1);
  const [schedulingServicesModalOpen, setSchedulingServicesModalOpen] = useState<boolean>(false);

  const limit = 50;
  const searchText = "";

  const events = useSelector((state: RootState) => state.scheduledServices.servicesForScheduling);
  const isLoadingMore = useSelector((state: RootState) => state.scheduledServices.loadingMoreServicesForScheduling);
  const eventsCount = useSelector((state: RootState) => state.scheduledServices.count);
  const selectedSiteId = useSelector((state:RootState) => state.scheduledServices.selectedSiteId);
  const servicesForSchedulingFilters = useSelector((state: RootState) => state.scheduledServices.filtersServicesForScheduling);
  const selectedServices = useSelector((state: RootState) => state.scheduledServices.selectedServicesToBeScheduled);

  const selectedAccess = useSelector((state: any) => state.auth.selectedAccess);

  const isAnalyst = selectedAccess.role === EAccessRole.analyst;
  const isContractor = selectedAccess.role === EAccessRole.contractor;
  const isInspector = selectedAccess.inspector && (isAnalyst || isContractor);

  const { areas, companies, equipmentGroups, equipmentModels, frequencies, places, serviceTypes, startDate, endDate, equipment, serviceStatus } = servicesForSchedulingFilters;

  useEffect(() => {
    dispatch(actions.fetchServicesForScheduling(page, limit, searchText, selectedSiteId, places, equipmentModels, equipmentGroups, areas, companies, serviceTypes, frequencies, startDate, endDate, equipment, false, serviceStatus))
  }, [limit, searchText, selectedSiteId, places, equipmentModels, equipmentGroups, areas, companies, serviceTypes, frequencies, startDate, endDate, equipment, serviceStatus]);

  useEffect(() => {
    if (page > 1) {
      dispatch(actions.fetchServicesForScheduling(page, limit, searchText, selectedSiteId, places, equipmentModels, equipmentGroups, areas, companies, serviceTypes, frequencies, startDate, endDate, equipment, true, serviceStatus))
    }
  }, [page]);


  let totalFilters = areas.length + companies.length + equipmentGroups.length + equipmentModels.length + frequencies.length + places.length + serviceTypes.length + serviceStatus.length;
  totalFilters += startDate ? 1 : 0;
  totalFilters += endDate ? 1 : 0;
  totalFilters += equipment ? 1 : 0;

  return (
    <>
      <Box sx={styles.container}>
        <Box sx={styles.titleContainer}>
          <Typography variant='h6'>{ i18n.t('scheduledServicesEventCardList.servicesTitle') as string }</Typography>
          <Badge badgeContent={totalFilters} color='error' invisible={totalFilters === 0}>
            <Button
              variant='contained'
              onClick={() => setSchedulingServicesModalOpen(true)}
              style={styles.buttonFilter}
            >
              { i18n.t('scheduledServicesEventCardList.servicesFilters') as string }<FilterList style={styles.buttonFilterIcon} />
            </Button>
          </Badge>
        </Box>
        <Box sx={{display: 'flex', justifyContent: 'space-between', marginTop: '16px', marginBottom: '8px', alignItems: 'center' }}>
          <Button
            startIcon={(events.length !== selectedServices.length) ? <AddIcon /> : <RemoveIcon />}
            color='primary'
            variant='outlined'
            onClick={() => {
              if (events.length !== selectedServices.length) dispatch(actions.scheduledServicesAddAllSelectedServicesTobeScheduled());
              else dispatch(actions.scheduledServicesClearSelectedServicesTobeScheduled());
            }}
            disabled={isInspector}
          >
            {(events.length !== selectedServices.length) ? i18n.t('scheduledServicesEventCardList.selectAll') as string : i18n.t('scheduledServicesEventCardList.clearAll') as string}
          </Button>
          { events.length > 0 && <Typography sx={{color: 'rgba(0, 0, 0, 0.38)' }}>{`${selectedServices.length}/${events.length} selecionados`}</Typography>}
        </Box>
        <Box sx={styles.cardContainer}>
          { events.length > 0 ?
            events.map(event => 
              <EventCard
                key={event._id + event.serialNumber + event.scheduledServicesReplicated.equipmentServiceId._id}
                equipment={event}
                goToEquipmentPage={goToEquipmentPage}
                disabled={isInspector}
              />
            )
            : <Typography>{i18n.t('scheduledServicesEventCardList.notFound') as string}</Typography>
          }
          {(eventsCount > events.length) && <LoadingButton onClick={() => setPage(page => page + 1)} variant='outlined' loading={isLoadingMore}>
            { i18n.t('scheduledServicesEventCardList.showMoreButton') as string }
          </LoadingButton>}
        </Box>
      </Box>
      <Drawer
        variant="temporary"
        anchor="right"
        onClose={() => setSchedulingServicesModalOpen(false)}
        open={schedulingServicesModalOpen}
      >
        <ServicesForSchedulingFiltersModal closeModal={() => setSchedulingServicesModalOpen(false)}  resetPage={() => setPage(1)}/> 
      </Drawer>
    </>
  )
};

export default EventCardList;

const styles = {
  container: {
    width: '28%',
    marginLeft: '10px',
    maxWidth: '30rem',
    minWidth: '25rem'
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '10px',
  },
  cardContainer: {
    width: '100%',
    paddingTop: '8px',
    paddingBottom: '8px',
    background: 'white',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '10px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '750px',
    overflowY: 'auto',
  },
  buttonFilterIcon: {
    marginLeft: '5px'
  },
  buttonFilter: {
    backgroundColor: 'green',
    height: '2.3rem',
    width: '13rem',
    padding: '1rem',
    color: '#fff',
  },
}
